import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// Rxjs
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// Services
import { WebcomponentsService } from '@core/service/webcomponents.service';

// Shared
import { ExtendTermService, SUCCESS_CODE_ENUMERATION } from '@credit/extend-term';

// Environments
import { environment } from '@environments/environment';

// Swal
import { show_popup } from '@shared/utility/popup-error';

@Component({
  selector: 'app-renegotiate-loan',
  templateUrl: './renegotiate-loan.component.html',
  styleUrls: ['./renegotiate-loan.component.scss'],
})
export class RenegotiateLoanComponent implements AfterViewInit, OnDestroy {
  @ViewChild('renegotiateLoanRef', { static: false }) public renegotiateLoanComponent: ElementRef;

  public loanId: string;
  public isLoading: boolean;

  private readonly unSubscribe$: Subject<void>;

  constructor(
    private readonly webComponentService: WebcomponentsService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly extendTermService: ExtendTermService
  ) {
    this.loanId = this.activatedRoute.snapshot.params['id'];
    this.isLoading = false;

    this.unSubscribe$ = new Subject<void>();
  }

  public ngAfterViewInit(): void {
    this.renderRenegotiateLoanWebComponent();
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  public renderRenegotiateLoanWebComponent(): void {
    const renegotiateLoan = this.renegotiateLoanComponent.nativeElement;
    this.webComponentService.isLoaded$.pipe(takeUntil(this.unSubscribe$)).subscribe({
      next: (isLoaded) => {
        if (isLoaded) {
          renegotiateLoan.ngElementStrategy.componentRef.instance.build({
            novationTypeId: environment.novationTypeId.RNE,
            loanId: this.loanId,
            isLoading: this.isLoading,
          });
        }
      },
    });
  }

  public backAction(): void {
    this.router.navigate(['/home']);
  }

  public continueButton(event: {
    detail: { requiredPaymentAction: boolean; novationTypeId: string; installmentsSelected: number };
  }): void {
    const { novationTypeId, installmentsSelected } = event.detail;
    this.isLoading = true;
    this.extendTermService
      .command({
        loanId: parseInt(this.loanId),
        novationType: novationTypeId,
        term: installmentsSelected,
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.unSubscribe$)
      )
      .subscribe({
        next: (response) => {
          if (this.isValidSuccessCode(response.result.code)) {
            const data = { isNovation: true, novationTypeId, loanId: this.loanId };
            event.detail.requiredPaymentAction
              ? this.router.navigate(['value-to-pay', this.loanId], {
                  queryParams: { novationTypeId: novationTypeId },
                })
              : this.router.navigate(['payment-status'], { queryParams: data });
          } else {
            show_popup('Error', response.result.message);
          }
        },
        error: () => {
          show_popup('Error', 'Ha ocurrido un error, intenta más tarde');
        },
      });
  }

  private isValidSuccessCode(code: string): boolean {
    return Object.values(SUCCESS_CODE_ENUMERATION).includes(code as SUCCESS_CODE_ENUMERATION);
  }
}
